/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { columns as datacenterColumns, Datacenter } from "./DatacenterData";
import { DatacenterViewModal } from "./Modals/DatacenterViewModal";
import { DatacenterEditModal } from "./Modals/DatacenterEditModal";
import { DatacenterCreateModal } from "./Modals/DatacenterCreateModal";

import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusNV.svg";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";
import {
  getAllDatacenters,
  getDatacenterById,
  createDatacenter,
  updateDatacenter,
  deleteDatacenter,
} from "./request/request";
import { getHardwareListByDataCenter } from "../Port/request/request";
import { getDataCentersIdsNames } from "../Hardware/request/request";
import { useAppSelector } from "@redux/hooks";
import { ParsedFilter as OldParsed, FilterField, buildPredicate, labelId, parseToken } from "../../../helpers/advancedFilters";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";

// ---------- Interfaces -----------
interface DataCenterItem {
  datacenterId: string; // or number
  name: string;
}

export default function DCConnectionsInventory() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  // 1) State for table data
  const [datacenters, setDatacenters] = useState<Datacenter[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // 2) Data center dropdown arrays (for future use if needed)
  const [dataCenters, setDataCenters] = useState<DataCenterItem[]>([]);
  
  // 3) Delete confirmation
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [datacenterToDelete, setDatacenterToDelete] = useState<Datacenter | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;

  // Rows per page state for UI table pagination (default 10)
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 7) View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [selectedDatacenter, setSelectedDatacenter] =
    useState<Datacenter | null>(null);

  // 8) Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [datacenterToEdit, setDatacenterToEdit] =
    useState<Datacenter | null>(null);

  // 9) Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    (async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();

        // A) Fetch all datacenters with hardware included
        console.log("[DatacenterPage] Mount: Fetching datacenters with hardware");
        const resp = await getAllDatacenters(token, true);
        console.log(resp.data);
        // Handle both direct array response and nested { data: [...] } response
        const datacentersData = Array.isArray(resp.data) 
        ? resp.data 
        : resp.data?.data || [];
        
        setDatacenters(datacentersData);
        const dcResp = await getDataCentersIdsNames(token);
        // Handle both direct array response and nested { data: [...] } response
        const dcRaw = Array.isArray((dcResp as any).data) 
          ? (dcResp as any).data 
          : (dcResp as any).data?.data || [];
        // The endpoint might return an array of objects with many fields
        // Shape/filter each object to { datacenterId, name }
        const shapedData: DataCenterItem[] = dcRaw.map(
          (dc: DataCenterItem) => ({
            datacenterId: dc.datacenterId,
            name: dc.name,
          })
        );
        setDataCenters(shapedData);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // ------------- Handler: Add -------------
  function handleAddDatacenter() {
    setIsCreateModalOpen(true);
  }

  // ------------- Handler: Create Datacenter -------------
  const handleCreateDatacenter = async (newData: Partial<Datacenter>) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await createDatacenter(token, newData);
      
      // Check if response indicates success
      if (response.data?.success === true || response.data?.data) {
        // Close modal first
        setIsCreateModalOpen(false);
        
        // Show skeleton and refetch all datacenters with includeHardware=true
        setIsFetching(true);
        try {
          const resp = await getAllDatacenters(token, true);
          // Handle both direct array response and nested { data: [...] } response
          const datacentersData = Array.isArray(resp.data) 
            ? resp.data 
            : resp.data?.data || [];
          
          setDatacenters(datacentersData);
        } catch (refetchError) {
          console.error("Error refetching datacenters after creation:", refetchError);
          // Even if refetch fails, we still consider creation successful
        } finally {
          setIsFetching(false);
        }
      } else {
        // If response doesn't indicate success, throw error
        throw new Error(response.data?.message || "Failed to create datacenter");
      }
    } catch (error: any) {
      console.error("Error creating datacenter:", error);
      // Extract error message
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || "An error occurred while creating the datacenter";
      throw new Error(errorMessage);
    }
  };

  const handleViewDatacenter = async (datacenter: Datacenter) => {
    // Open modal immediately and show skeleton
    setIsViewModalOpen(true);
    setIsViewLoading(true);
    setSelectedDatacenter(null); // Clear previous data
    
    try {
      const token = await getAccessTokenSilently();
      const response = await getDatacenterById(token, datacenter.datacenterId);
      // Handle both direct response and nested { data: {...} } response
      const datacenterData = response.data?.data || response.data;
      
      // Always fetch hardware list to get the count
      try {
        const hwResp = await getHardwareListByDataCenter(token, datacenter.datacenterId);
        // Handle both direct array response and nested { data: [...] } response
        const hardwareArray = Array.isArray(hwResp.data) 
          ? hwResp.data 
          : (hwResp.data?.data || []);
        const hardwareCount = hardwareArray.length;
        setSelectedDatacenter({
          ...datacenterData,
          numberOfHardware: hardwareCount,
        });
      } catch (err) {
        console.error(`Error fetching hardware for ${datacenter.datacenterId}:`, err);
        setSelectedDatacenter({
          ...datacenterData,
          numberOfHardware: 0,
        });
      }
    } catch (error) {
      console.error("Error viewing datacenter:", error);
      // Keep modal open but show error state (or close it, depending on UX preference)
    } finally {
      setIsViewLoading(false);
    }
  };
  // ------------- Handler: Edit -------------
  const handleEditDatacenter = async (datacenter: Datacenter) => {
    // Open modal immediately and show skeleton
    setIsEditModalOpen(true);
    setIsEditLoading(true);
    setDatacenterToEdit(null); // Clear previous data
    
    try {
      const token = await getAccessTokenSilently();
      const response = await getDatacenterById(token, datacenter.datacenterId);
      // Handle both direct response and nested { data: {...} } response
      const datacenterData = response.data?.data || response.data;
      
      // Always fetch hardware list to get the count
      try {
        console.log("[DatacenterPage] handleEditDatacenter: Fetching hardware for", datacenter.datacenterId);
        const hwResp = await getHardwareListByDataCenter(token, datacenter.datacenterId);
        // Handle both direct array response and nested { data: [...] } response
        const hardwareArray = Array.isArray(hwResp.data) 
          ? hwResp.data 
          : (hwResp.data?.data || []);
        const hardwareCount = hardwareArray.length;
        setDatacenterToEdit({
          ...datacenterData,
          numberOfHardware: hardwareCount,
        });
      } catch (err) {
        console.error(`Error fetching hardware for ${datacenter.datacenterId}:`, err);
        setDatacenterToEdit({
          ...datacenterData,
          numberOfHardware: 0,
        });
      }
    } catch (error) {
      console.error("Error loading datacenter for edit:", error);
    } finally {
      setIsEditLoading(false);
    }
  };

  // ------------- Handler: Save Datacenter -------------
  const handleSaveDatacenter = async (updatedData: Partial<Datacenter>) => {
    if (!datacenterToEdit) return;
    
    try {
      const token = await getAccessTokenSilently();
      const response = await updateDatacenter(token, datacenterToEdit.datacenterId, updatedData);
      
      // Check if response indicates success
      if (response.data?.success === true || response.status === 200) {
        // Close modal first
        setIsEditModalOpen(false);
        
        // Show skeleton and refetch all datacenters with includeHardware=true
        setIsFetching(true);
        try {
          const resp = await getAllDatacenters(token, true);
          // Handle both direct array response and nested { data: [...] } response
          const datacentersData = Array.isArray(resp.data) 
            ? resp.data 
            : resp.data?.data || [];
          
          setDatacenters(datacentersData);
        } catch (refetchError) {
          console.error("Error refetching datacenters after update:", refetchError);
          // Even if refetch fails, we still consider update successful
        } finally {
          setIsFetching(false);
        }
      } else {
        // If response doesn't indicate success, throw error
        throw new Error(response.data?.message || "Failed to update datacenter");
      }
    } catch (error: any) {
      console.error("Error saving datacenter:", error);
      // Extract error message
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || "An error occurred while updating the datacenter";
      throw new Error(errorMessage);
    }
  };

  // ------------- Handler: Delete -------------
  const handleDeleteDatacenter = (datacenter: Datacenter) => {
    setDatacenterToDelete(datacenter);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!datacenterToDelete) return;

    try {
      setIsDeleting(true);
      const token = await getAccessTokenSilently();
      const response = await deleteDatacenter(token, datacenterToDelete.datacenterId);
      
      // Check if response indicates success
      if (response.data?.success === true || response.status === 200 || response.status === 204) {
        // Close the alert first
        setIsAlertOpen(false);
        setDatacenterToDelete(null);
        
        // Show skeleton and refetch all datacenters with includeHardware=true
        setIsFetching(true);
        try {
          const resp = await getAllDatacenters(token, true);
          // Handle both direct array response and nested { data: [...] } response
          const datacentersData = Array.isArray(resp.data) 
            ? resp.data 
            : resp.data?.data || [];
          
          setDatacenters(datacentersData);
        } catch (refetchError) {
          console.error("Error refetching datacenters after delete:", refetchError);
          // Even if refetch fails, we still consider delete successful
        } finally {
          setIsFetching(false);
        }
      } else {
        throw new Error(response.data?.message || "Failed to delete datacenter");
      }
    } catch (error: any) {
      console.error("Error deleting datacenter:", error);
      // Extract error message
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || "An error occurred while deleting the datacenter";
      alert(errorMessage); // You might want to use a better error handling mechanism
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDatacenterToDelete(null);
    setIsAlertOpen(false);
  };

  // ------------------------------------------------------------------------------------
  // Filter fields for GenericSearchbar
  // ------------------------------------------------------------------------------------
  const datacenterFields: FilterField<Datacenter>[] = useMemo(() => [
    { label: "Datacenter ID", accessor: (row) => row.datacenterId, type: "string" },
    { label: "Name", accessor: (row) => row.name, type: "string" },
    { label: "Address", accessor: (row) => row.direction, type: "string" },
    { label: "Provider", accessor: (row) => row.provider?.name || "", type: "string" },
    { label: "Status", accessor: (row) => row.status, type: "string" },
    { label: "Number of Hardware", accessor: (row) => row.hardwareCount ?? row.numberOfHardware ?? 0, type: "number" },
  ], []);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of datacenterFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [datacenterFields]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Datacenter>>();
    for (const f of datacenterFields) {
      const id = f.key ?? labelId(f.label);
      m.set(id.toLowerCase(), f);
    }
    return m;
  }, [datacenterFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":"; // contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<Datacenter>(normalizedFilters, datacenterFields),
    [normalizedFilters, datacenterFields]
  );

  const filteredDatacenters = useMemo(
    () => datacenters.filter(predicate),
    [datacenters, predicate]
  );

  // ------------------------------------------------------------------------------------
  // Override "Actions" column
  // ------------------------------------------------------------------------------------
  const columns = datacenterColumns.map((col) => {
    if (col.header === "Actions" && col.renderCell) {
      return {
        ...col,
        renderCell: (row: Datacenter) => (
          <div className="flex items-center gap-2">
            {/* View */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleViewDatacenter(row)}
            >
              <IconEye className="w-6 h-6" />
            </button>

            {/* Edit */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleEditDatacenter(row)}
            >
              <IconEdit className="w-10 h-10" />
            </button>

            {/* Delete */}
            <button
              className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-red-50
                text-red-600
                hover:bg-red-100
              "
              onClick={() => handleDeleteDatacenter(row)}
            >
              <IconDelete className="w-6 h-6" />
            </button>
          </div>
        ),
      };
    }
    return col;
  });

  const suggestedKeys = useMemo(() => {
    const headers = datacenterColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [datacenterColumns, isViewingAllCustomers]);

  return (
    <div className="relative p-1 sm:p-4 overflow-hidden flex flex-col h-full">
      {/* Card con tabla */}
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              {/* Searchbar y botón */}
              <div className="mb-2 sm:mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex items-center justify-between gap-4 mt-4 mb-10">
                  <div className="w-[520px]">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                  <GenericButton
                    label={
                      <div className="flex items-center gap-2">
                        <IconPlus className="w-4 h-4" />
                        <span>Add Datacenter</span>
                      </div>
                    }
                    variant="brand"
                    size="md"
                    onClick={handleAddDatacenter}
                  />
                </div>
              </div>

              {/* Table wrapper */}
              <div className="flex-1 min-h-0 flex flex-col">
                {isFetching ? (
                  <TableSkeleton columns={5} rows={5} />
                ) : (
                  <GenericTable
                    columns={columns}
                    data={filteredDatacenters}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={setRowsPerPage}
                    width="100%"
                    maxTableBodyHeight="100%"
                  />
                )}
              </div>
            </div>
          }
        />
      </div>

      {/* View Datacenter Modal */}
      <DatacenterViewModal
        isOpen={isViewModalOpen}
        isLoading={isViewLoading}
        datacenter={selectedDatacenter}
        onClose={() => setIsViewModalOpen(false)}
      />

      {/* Edit Datacenter Modal */}
      <DatacenterEditModal
        isOpen={isEditModalOpen}
        isLoading={isEditLoading}
        datacenter={datacenterToEdit}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveDatacenter}
      />

      {/* Create Datacenter Modal */}
      <DatacenterCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateDatacenter}
      />

      {/* Delete Confirmation */}
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the datacenter."
        cancelLabel="Cancel"
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        confirmButtonDisabled={isDeleting}
      />
    </div>
  );
}
